import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import classNames from "classnames";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../store/hooks";
import HorizontalPadding from "../../components/HorizontalPadding";
import { BranchNoteDTO, PublishedVersionDTO } from "../../interfaces/Dtos/ReleaseNotesDTO";
import ReleaseNoteSections, { formatFecha } from "./ReleaseNoteSections";
import {
  compareVersions,
  loadReleaseNotes,
  markLatestSeen,
  setCurrentUser,
  unreadWithAction,
  useReleaseNotesState,
} from "./releaseNotesStore";

const GITHUB_PRS = "https://github.com/Tealca-Devs/Innovus/pulls";
const minorOf = (v: string) => v.split(".").slice(0, 2).join(".");

/**
 * Lista de versiones agrupada por menor (1.20.x, 1.19.x); el grupo de la elegida, abierto.
 * «Requiere acción» se marca solo en las versiones que el usuario no había visto al abrir la página.
 * La elegida usa el mismo estilo que el ítem activo del menú (bg-gray-50 text-indigo-600):
 * tailwind.config.js solo cambia indigo-500/600 por el color del ambiente.
 */
const VersionList: FC<{
  versions: PublishedVersionDTO[];
  selected: string;
  myVersion: string;
  pending: string[];
  onSelect: (v: string) => void;
}> = ({ versions, selected, myVersion, pending, onSelect }) => {
  const groups = useMemo(() => {
    const out: { minor: string; items: PublishedVersionDTO[] }[] = [];
    for (const v of versions) {
      const m = minorOf(v.version);
      if (out[out.length - 1]?.minor !== m) out.push({ minor: m, items: [] });
      out[out.length - 1].items.push(v);
    }
    return out;
  }, [versions]);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const isOpen = (m: string) => open[m] ?? m === minorOf(selected);

  return (
    <>
      {/* Pantallas angostas: un selector sobre el detalle. */}
      <label className="md:hidden flex flex-col gap-1 text-sm font-semibold text-gray-700">
        Versión
        <select
          className="rounded-md border-gray-300 text-base"
          value={selected}
          onChange={(e) => onSelect(e.target.value)}
        >
          {versions.map((v) => (
            <option key={v.version} value={v.version}>
              {v.version} · {formatFecha(v.fecha)}
              {v.version === myVersion ? " · tu versión" : ""}
              {pending.includes(v.version) ? " · requiere acción" : ""}
            </option>
          ))}
        </select>
      </label>

      <nav aria-label="Versiones" className="hidden md:flex w-56 shrink-0 flex-col gap-1">
        {groups.map((g) => (
          <div key={g.minor}>
            <button
              type="button"
              className="flex w-full items-center gap-1 px-2 py-1 text-xs font-semibold tracking-wide text-gray-500 hover:text-gray-700"
              aria-expanded={isOpen(g.minor)}
              onClick={() => setOpen({ ...open, [g.minor]: !isOpen(g.minor) })}
            >
              {isOpen(g.minor) ? (
                <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
              )}
              {g.minor}.x
            </button>
            {isOpen(g.minor) && (
              <ul className="flex flex-col gap-0.5">
                {g.items.map((v) => (
                  <li key={v.version}>
                    <button
                      type="button"
                      aria-current={v.version === selected ? "page" : undefined}
                      onClick={() => onSelect(v.version)}
                      className={classNames(
                        "w-full rounded-md px-3 py-2 text-left",
                        v.version === selected
                          ? "bg-gray-50 text-indigo-600"
                          : "text-gray-700 hover:bg-gray-50"
                      )}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="font-semibold">{v.version}</span>
                        {pending.includes(v.version) && (
                          <span className="flex items-center" title="Requiere acción">
                            <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
                            <span className="sr-only">Requiere acción</span>
                          </span>
                        )}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {formatFecha(v.fecha)}
                        {v.version === myVersion && " · tu versión"}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </nav>
    </>
  );
};

const Notice: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">{children}</div>
);

/** Desarrollo: la nota de cada rama que está en dev. */
const DevBlock: FC<{ cards: BranchNoteDTO[] }> = ({ cards }) => {
  if (!cards.length) return null;
  return (
    <section className="flex flex-col gap-4" aria-label="En pruebas en desarrollo">
      <h2 className="text-xl font-bold text-gray-800">En pruebas en desarrollo</h2>
      <Notice>
        Esto no es la próxima versión: son los cambios que se prueban ahora; el comité decide qué sale
        en cada release.
      </Notice>
      {cards.map((card) => (
        <article key={card.rama} className="flex flex-col gap-3 rounded-lg border bg-white p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <span className="font-mono text-sm font-semibold text-gray-800">{card.rama}</span>
            <a
              className="text-sm font-semibold text-indigo-600 underline"
              href={`${GITHUB_PRS}?q=${encodeURIComponent(`is:pr head:${card.rama}`)}`}
              target="_blank"
              rel="noreferrer"
            >
              Ver PR en GitHub
            </a>
          </div>
          <ReleaseNoteSections sections={card} showInternal />
        </article>
      ))}
    </section>
  );
};

const Novedades: FC = () => {
  const { version: requested } = useParams();
  const navigate = useNavigate();
  const userLogin = useAppSelector((s) => s.user.user?.userLogin) ?? null;
  const { status, data, seen } = useReleaseNotesState();
  const myVersion = process.env.REACT_APP_VERSION || "";
  const isProduction = data?.environment === "production";

  useEffect(() => {
    setCurrentUser(userLogin);
    loadReleaseNotes();
  }, [userLogin]);

  const published = data?.published ?? [];
  const latest = published[0]?.version;
  const found = requested ? published.find((v) => v.version === requested) : undefined;
  const selected = found ?? published[0];

  // Lo que estaba sin leer al abrir la página, antes de marcarlo visto.
  const pendingActions = useRef<string[] | null>(null);
  if (pendingActions.current === null && status === "ready" && seen !== undefined) {
    pendingActions.current = unreadWithAction({ data, seen }).map((v) => v.version);
  }

  // Se marca visto solo con la última versión en pantalla, nunca en carga, error o una vieja.
  useEffect(() => {
    if (status === "ready" && selected && selected.version === latest) markLatestSeen();
  }, [status, selected, latest]);

  const select = (v: string) => navigate(`/novedades/${v}`, { replace: true });
  const otherActions = (pendingActions.current ?? []).filter((v) => v !== selected?.version);
  const myPublished = published.find((v) => v.version === myVersion);
  const outdated = !!myPublished && !!latest && compareVersions(myVersion, latest) < 0;

  return (
    <main className="lg:pl-72">
      <div className="py-6 sm:px-6 lg:px-8 bg-white relative flex items-center justify-between h-32 mb-8">
        <header className="ml-4">
          <h1 className="text-2xl font-bold text-gray-700">Novedades</h1>
          <p className="text-gray-500">Lo que cambió en cada versión de Innovus.</p>
        </header>
      </div>

      <HorizontalPadding>
        {status === "loading" || status === "idle" ? (
          <div className="flex gap-8 animate-pulse" aria-busy="true" aria-label="Cargando novedades">
            <div className="hidden md:block w-56 h-64 rounded-md bg-gray-100" />
            <div className="flex-1 h-64 rounded-lg bg-gray-100" />
          </div>
        ) : status === "error" ? (
          <div className="flex flex-col items-start gap-3 rounded-lg border bg-white p-6" role="alert">
            <p className="font-semibold text-gray-800">No se pudieron cargar las novedades.</p>
            {process.env.REACT_APP_PRODUCTION !== "true" && (
              <p className="text-sm text-gray-600">
                En local, genéralo con <code>npm run release-notes:local</code>.
              </p>
            )}
            <button
              type="button"
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
              onClick={() => loadReleaseNotes(true)}
            >
              Reintentar
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            {(data?.environment === "development" || data?.environment === "pr") && (
              <DevBlock cards={data.inTesting ?? []} />
            )}
            {!!data?.upcoming && (
              <section
                className="flex flex-col gap-4 rounded-lg border-2 border-dashed border-slate-400 bg-white p-6"
                aria-label="Próxima versión"
              >
                <div className="flex flex-wrap items-baseline gap-3">
                  <h2 className="text-xl font-bold text-gray-800">
                    Próxima versión · {data.upcoming.version}
                  </h2>
                  <span className="rounded-full bg-slate-600 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-white">
                    En pruebas (número provisional)
                  </span>
                </div>
                <p className="text-sm text-slate-600">
                  Así la verán los usuarios cuando se publique. Tu versión: {myVersion || data.current}.
                </p>
                <ReleaseNoteSections sections={data.upcoming} showInternal />
              </section>
            )}

            {!published.length ? (
              <p className="text-gray-600">Aún no hay versiones publicadas.</p>
            ) : (
              <div className="flex flex-col gap-4">
                {!isProduction && <h2 className="text-lg font-bold text-gray-700">Versiones publicadas</h2>}
                {requested && !found && (
                  <Notice>
                    No encontramos la versión {requested}; te mostramos la más reciente.
                  </Notice>
                )}
                {otherActions.length > 0 && (
                  <Notice>
                    Estas versiones piden que hagas algo:{" "}
                    {otherActions.map((v, i) => (
                      <span key={v}>
                        {i > 0 && ", "}
                        <Link className="font-semibold text-indigo-600 underline" to={`/novedades/${v}`} replace>
                          {v}
                        </Link>
                      </span>
                    ))}
                  </Notice>
                )}
                <div className="flex flex-col md:flex-row gap-6 md:gap-8 md:items-start">
                  <VersionList
                    versions={published}
                    selected={selected!.version}
                    myVersion={myVersion}
                    pending={pendingActions.current ?? []}
                    onSelect={select}
                  />
                  <article className="flex-1 min-w-0 flex flex-col gap-5 rounded-lg border bg-white p-6">
                    <div className="flex flex-wrap items-baseline gap-3">
                      <h2 className="text-xl font-bold text-gray-800">Versión {selected!.version}</h2>
                      <span className="text-gray-500">{formatFecha(selected!.fecha)}</span>
                    </div>
                    {outdated && selected!.version === latest && (
                      <Notice>
                        Tu Innovus está en la {myVersion}; recarga la página para actualizar.
                      </Notice>
                    )}
                    <ReleaseNoteSections sections={selected!} showInternal={!isProduction} />
                  </article>
                </div>
              </div>
            )}
          </div>
        )}
      </HorizontalPadding>
    </main>
  );
};

export default Novedades;
